import React, { useEffect, useState, useRef, useCallback } from 'react';
import './PdfProgressModal.css';
import type { ExportType } from '../types/exportPreflight';

interface PdfProgressProps {
  storybookId: string;
  exportType?: ExportType;
  bleed?: boolean;
  onClose: () => void;
  onGenerate: () => void;
  onError: (error: string) => void;
}

interface DownloadInfo {
  url: string;
  expiresAt: string;
  filename: string;
}

const PROGRESS_MESSAGES = [
  'Starting PDF generation...',
  'Preparing storybook pages...',
  'Loading images...',
  'Processing content...',
  'Rendering pages...',
  'Generating PDF layout...',
  'Optimizing output...',
  'Almost ready...',
  'Finalizing PDF...',
  'This may take another moment...'
];

const PdfProgressModal: React.FC<PdfProgressProps> = ({ 
  storybookId,
  exportType = 'full',
  bleed = false,
  onClose,
  onGenerate,
  onError 
}) => {
  const [cachedDownload, setCachedDownload] = useState<DownloadInfo | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [estimatedProgress, setEstimatedProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Starting PDF generation...');
  const [error, setError] = useState<string | null>(null);
  const [downloadInfo, setDownloadInfo] = useState<DownloadInfo | null>(null);
  const [isCheckingCache, setIsCheckingCache] = useState(true);
  
  const isGeneratingRef = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const generationStartTimeRef = useRef<number>(0); // Track when we started generation to ignore old cached PDFs

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const checkCachedPdf = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/storybooks/${storybookId}/pdf-download-url?exportType=${exportType}&bleed=${bleed ? 'true' : 'false'}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.downloadUrl) {
            const expiresAt = new Date(data.expiresAt);
            if (expiresAt > new Date()) {
              console.log('[PDF] Found valid cached PDF');
              setCachedDownload({
                url: data.downloadUrl,
                expiresAt: data.expiresAt,
                filename: data.filename
              });
            }
          }
        }
      } catch (err) {
        console.log('[PDF] No cached PDF found');
      }
      setIsCheckingCache(false);
    };
    
    checkCachedPdf();
  }, [storybookId, exportType, bleed, API_URL]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  const checkPdfReady = useCallback(async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/storybooks/${storybookId}/pdf-download-url?exportType=${exportType}&bleed=${bleed ? 'true' : 'false'}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          // Check if this PDF was created after we started generation
          // The filename contains a timestamp: pdfs/{id}/{timestamp}_{title}.pdf
          const filename = data.filename || '';
          const timestampMatch = filename.match(/^(\d+)_/);
          
          if (timestampMatch && generationStartTimeRef.current > 0) {
            const pdfTimestamp = parseInt(timestampMatch[1], 10);
            if (pdfTimestamp < generationStartTimeRef.current) {
              console.log(`[PDF] Found old cached PDF (${pdfTimestamp}) from before generation started (${generationStartTimeRef.current}), ignoring`);
              return false;
            }
          }
          
          console.log('[PDF] Generation complete!');
          setDownloadInfo({
            url: data.downloadUrl,
            expiresAt: data.expiresAt,
            filename: data.filename
          });
          setCachedDownload({
            url: data.downloadUrl,
            expiresAt: data.expiresAt,
            filename: data.filename
          });
          setEstimatedProgress(100);
          setProgressMessage('PDF generation complete!');
          setIsGenerating(false);
          isGeneratingRef.current = false;
          
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          
          return true;
        }
      }
      return false;
    } catch (err) {
      return false;
    }
  }, [API_URL, storybookId, exportType, bleed]);

  const startGeneration = useCallback(() => {
    if (isGeneratingRef.current) {
      console.log('[PDF] Already generating, ignoring click');
      return;
    }
    
    console.log('[PDF] Starting generation...');
    isGeneratingRef.current = true;
    setIsGenerating(true);
    setEstimatedProgress(0);
    setProgressMessage('Starting PDF generation...');
    setError(null);
    setDownloadInfo(null);
    setCachedDownload(null); // Clear cached download when regenerating
    startTimeRef.current = Date.now();
    generationStartTimeRef.current = Date.now(); // Track when we started to ignore old cached PDFs
    
    onGenerate();
    
    progressIntervalRef.current = setInterval(() => {
      if (!isGeneratingRef.current) return;
      
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const progress = Math.min(95, Math.floor((1 - Math.exp(-elapsed / 45)) * 100));
      const messageIndex = Math.min(Math.floor(progress / 10), PROGRESS_MESSAGES.length - 1);
      
      setEstimatedProgress(progress);
      setProgressMessage(PROGRESS_MESSAGES[messageIndex]);
    }, 1000);
    
    pollIntervalRef.current = setInterval(async () => {
      if (!isGeneratingRef.current) return;
      
      const ready = await checkPdfReady();
      if (ready) {
        console.log('[PDF] PDF is ready, stopping polling');
      }
    }, 5000);
    
    setTimeout(() => {
      if (isGeneratingRef.current) {
        checkPdfReady();
      }
    }, 2000);
    
    setTimeout(() => {
      if (isGeneratingRef.current) {
        console.log('[PDF] Generation timed out');
        setError('PDF generation timed out. Please try again.');
        onError('PDF generation timed out');
        setIsGenerating(false);
        isGeneratingRef.current = false;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      }
    }, 600000);
    
  }, [onGenerate, onError, checkPdfReady]);

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const formatExpiryTime = (expiresAt: string) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const diffMinutes = Math.floor((date.getTime() - now.getTime()) / 60000);
    if (diffMinutes < 1) return 'less than 1 minute';
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
  };

  const getExportTypeLabel = () => {
    switch (exportType) {
      case 'manuscript': return 'Manuscript';
      case 'front-cover': return 'Front Cover';
      case 'back-cover': return 'Back Cover';
      default: return 'Complete Book';
    }
  };

  if (isCheckingCache) {
    return (
      <div className="pdf-progress-modal-overlay">
        <div className="pdf-progress-modal">
          <h3>📄 {getExportTypeLabel()} PDF</h3>
          <div className="pdf-progress-spinner">
            <div className="spinner"></div>
            <p style={{ marginTop: '16px', color: '#666' }}>Checking for existing PDF...</p>
          </div>
        </div>
      </div>
    );
  }

  if (downloadInfo) {
    return (
      <div className="pdf-progress-modal-overlay">
        <div className="pdf-progress-modal">
          <h3>✅ PDF Ready!</h3>
          <div className="pdf-download-info">
            <p className="pdf-download-filename">{downloadInfo.filename}</p>
            <p className="pdf-download-expiry">
              Link expires in {formatExpiryTime(downloadInfo.expiresAt)}
            </p>
            
            <div className="pdf-action-buttons">
              <button 
                className="btn btn-primary btn-download"
                onClick={() => handleDownload(downloadInfo.url)}
              >
                📥 Download PDF
              </button>
              <button 
                className="btn btn-secondary btn-regenerate"
                onClick={() => {
                  setDownloadInfo(null);
                  startGeneration();
                }}
                disabled={isGenerating}
              >
                🔄 Generate Again
              </button>
            </div>
            
            <button className="btn btn-secondary btn-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="pdf-progress-modal-overlay">
        <div className="pdf-progress-modal">
          <h3>⏳ Generating PDF</h3>
          
          {error ? (
            <div className="pdf-progress-error">
              <p>❌ {error}</p>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="pdf-progress-bar-container">
                <div 
                  className="pdf-progress-bar"
                  style={{ width: `${estimatedProgress}%` }}
                />
              </div>
              
              <div className="pdf-progress-details">
                <p className="pdf-progress-message">{progressMessage}</p>
                <p className="pdf-progress-percentage">{estimatedProgress}% complete</p>
                <p className="pdf-progress-note">
                  This typically takes 1-2 minutes depending on storybook size.
                </p>
              </div>

              <div className="pdf-progress-spinner">
                <div className="spinner"></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-progress-modal-overlay">
      <div className="pdf-progress-modal">
        <h3>📄 {getExportTypeLabel()} PDF</h3>
        
        {cachedDownload ? (
          <div className="pdf-cached-section">
            <div className="pdf-cached-info">
              <p className="pdf-cached-label">✅ Previously Generated PDF Available</p>
              <p className="pdf-download-filename">{cachedDownload.filename}</p>
              <p className="pdf-download-expiry">
                Link expires in {formatExpiryTime(cachedDownload.expiresAt)}
              </p>
            </div>
            
            <div className="pdf-action-buttons">
              <button 
                className="btn btn-primary btn-download"
                onClick={() => handleDownload(cachedDownload.url)}
              >
                📥 Download Existing PDF
              </button>
              <button 
                className="btn btn-secondary btn-regenerate"
                onClick={startGeneration}
                disabled={isGenerating}
              >
                🔄 Regenerate PDF
              </button>
            </div>
          </div>
        ) : (
          <div className="pdf-no-cache-section">
            <p className="pdf-no-cache-message">
              No existing PDF found. Generate one now?
            </p>
            <button 
              className="btn btn-primary"
              onClick={startGeneration}
              disabled={isGenerating}
            >
              �� Generate PDF
            </button>
          </div>
        )}
        
        <button className="btn btn-secondary btn-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default PdfProgressModal;
